import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  concatMap,
  EMPTY,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { BrowserStorageService } from './browser-storage.service';
import { JwtService } from './jwt.service';
import { AuthService } from './auth.service';
import { environment } from 'environments/environment';

const CHECK_TOKEN = new HttpContextToken<boolean>(() => true);
const REFRESH_TOKEN = new HttpContextToken<boolean>(() => false);

export const noCheckToken = () => {
  return new HttpContext().set(CHECK_TOKEN, false);
};

export const refreshTokenContext = () => {
  return new HttpContext().set(REFRESH_TOKEN, true);
};

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor {
  url: string = environment.appUrlTourbohome;
  private isRedirecting2FA = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private jwtService: JwtService,
    private browserStorageService: BrowserStorageService,
  ) {}
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isRequestNeedToken = req.context.get(CHECK_TOKEN);
    if (!isRequestNeedToken) {
      return next.handle(req);
    }

    const isValidToken = this.jwtService.isValidToken();
    const isValidRefreshToken = this.jwtService.isValidRefreshToken();

    if (isValidToken) {
      // Token válido, sigue normalmente
      return this.addToken(req, next);
    }

    if (isValidRefreshToken) {
      // Token vencido pero refresh válido, refresca y reintenta
      return this.authService.refreshToken().pipe(
        concatMap((response) => {
          // Mapeo para compatibilidad con la respuesta del backend
          const token = response?.data?.access_token || response?.token;
          const refreshToken =
            response?.data?.refresh_token || response?.refresh_token;
          if (token) {
            this.browserStorageService.set('token', token);
          }
          if (refreshToken) {
            this.browserStorageService.set('refresh_token', refreshToken);
          }
          return this.addToken(req, next);
        }),
      );
    }

    // Ambos vencidos, logout
    this.authService.logout();
    if (
      window.location.href.includes('tourbo.com') ||
      window.location.href.includes('localhost')
    ) {
      window.location.href = this.url + '/auth/login';
    } else {
      window.location.href = '/auth';
    }
    return EMPTY;
  }

  private handle2FAHeaders(headers: HttpHeaders): boolean {
    const setup2fa =
      headers.get('X-2FA-Setup-Required') ||
      headers.get('x-2fa-setup-required');
    const require2fa =
      headers.get('X-2FA-Required') || headers.get('x-2fa-required');

    if (setup2fa === 'true' || require2fa === 'true') {
      if (!this.isRedirecting2FA) {
        this.isRedirecting2FA = true;
        const action = setup2fa === 'true' ? 'setup' : 'verify';
        this.authService.logout();
        localStorage.setItem('pending_2fa_action', action);
        if (
          window.location.href.includes('tourbo.com') ||
          window.location.href.includes('localhost')
        ) {
          window.location.href = this.url + '/auth/login';
        } else {
          window.location.href = '/auth';
        }
      }
      return true;
    }
    return false;
  }

  private addToken(request: HttpRequest<any>, next: HttpHandler) {
    const token = this.browserStorageService.get('token');
    if (token) {
      request = request.clone({
        setHeaders: { authorization: `Bearer ${token}` },
        withCredentials: true,
      });
    }

    return next.handle(request).pipe(
      switchMap((event) => {
        if (
          event instanceof HttpResponse &&
          this.handle2FAHeaders(event.headers)
        ) {
          return EMPTY;
        }
        return of(event);
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.handle2FAHeaders(error.headers)) return EMPTY;
        return throwError(() => error);
      }),
    );
  }
}
